import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

// Create Wallet Context
const WalletContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Custom hook to use Wallet Context
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Wallet Provider Component
export const WalletProvider = ({ children }) => {
  const { user } = useAuth();
  const [walletData, setWalletData] = useState({
    income: [],
    spending: [],
    monthlyBalance: 0,
    totalIncome: 0,
    totalSpending: 0
  });
  const [loading, setLoading] = useState(false);

  // Calculate totals when data changes
  useEffect(() => {
    const totalIncome = walletData.income.reduce((sum, item) => sum + item.amount, 0);
    const totalSpending = walletData.spending.reduce((sum, item) => sum + item.amount, 0);
    const monthlyBalance = totalIncome - totalSpending;

    setWalletData(prev => ({
      ...prev,
      totalIncome,
      totalSpending,
      monthlyBalance
    }));
  }, [walletData.income, walletData.spending]);

      // Fetch real wallet data from bookings
  const fetchWalletData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Get all bookings for current user
      const token = localStorage.getItem('skillsos_token');
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bookings = response.data;
      
      // Process bookings into income and spending
      const income = [];
      const spending = [];

      bookings.forEach(booking => {
        // Only process completed bookings
        if (booking.status === 'completed') {
          
          // Income: bookings where I'm the provider
          if (booking.provider_id === user.id) {
            income.push({
              id: booking.id,
              description: `${booking.service_category} - ${booking.requester_name}`,
              amount: booking.total_amount,
              date: booking.date,
              category: booking.service_category
            });
          }
          
          // Spending: bookings where I'm the requester
          if (booking.requester_id === user.id) {
            spending.push({
              id: booking.id,
              description: `${booking.service_category} - ${booking.provider_name}`,
              amount: booking.total_amount,
              date: booking.date,
              category: booking.service_category
            });
          }
        }
      });

      setWalletData(prev => ({
        ...prev,
        income,
        spending
      }));
      
      console.log('💰 Wallet data loaded:', { income: income.length, spending: spending.length });
      
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add income entry
  const addIncome = (entry) => {
    const newEntry = {
      ...entry,
      id: Date.now(),
      date: entry.date || new Date().toISOString().split('T')[0]
    };
    setWalletData(prev => ({
      ...prev,
      income: [...prev.income, newEntry]
    }));
  };

  // Add spending entry
  const addSpending = (entry) => {
    const newEntry = {
      ...entry,
      id: Date.now(),
      date: entry.date || new Date().toISOString().split('T')[0]
    };
    setWalletData(prev => ({
      ...prev,
      spending: [...prev.spending, newEntry]
    }));
  };
  
  // Process payment transaction (adds spending for client, income for specialist)
  const processPaymentTransaction = (paymentResult, breakdown, appointment) => {
    const transactionDate = new Date().toISOString().split('T')[0];
    
    // Add spending for client
    const clientSpending = {
      id: `txn_${Date.now()}_client`,
      description: `${appointment.service_category} - ${appointment.specialist_name}`,
      amount: breakdown.clientTotal,
      date: transactionDate,
      category: appointment.service_category,
      transactionId: paymentResult.transactionId,
      type: 'payment'
    };
    
    addSpending(clientSpending);
    
    // Store in localStorage for persistence
    try {
      const existingTransactions = JSON.parse(localStorage.getItem('wallet_transactions') || '[]');
      existingTransactions.push({
        ...clientSpending,
        userType: 'client',
        appointmentId: appointment.id
      });
      localStorage.setItem('wallet_transactions', JSON.stringify(existingTransactions));
    } catch (error) {
      console.warn('Failed to save transaction to localStorage:', error);
    }
    
    return clientSpending;
  };

  // Remove entry
  const removeEntry = (id, type) => {
    setWalletData(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item.id !== id)
    }));
  };

  // Load data when user changes
  useEffect(() => {
    if (user) {
      fetchWalletData();
    }
  }, [user]);

  const value = {
    walletData,
    loading,
    addIncome,
    addSpending,
    removeEntry,
    refreshData: fetchWalletData,
    processPaymentTransaction
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};